/**
 * Shared UI component for UI Address Form.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';

import { AddressDetails } from '../../models/address.types';
import { AddressLookupOption, AddressLookupService } from '../../../features/agencies/address-lookup.service';
import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';

/**
 * COMPONENT: Ui Address Form
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-address-form',
  standalone: true,
  imports: [UiAutocompleteFieldComponent, UiFormFieldComponent, TranslatePipe],
  templateUrl: './ui-address-form.component.html',
  styleUrl: './ui-address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAddressFormComponent {
  private readonly addressLookupService = inject(AddressLookupService);
  private readonly translateService = inject(TranslateService);
  private readonly activeLanguage = toSignal(
    this.translateService.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translateService.currentLang || this.translateService.getDefaultLang() || 'en')
    ),
    { initialValue: this.translateService.currentLang || this.translateService.getDefaultLang() || 'en' }
  );
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly value = input.required<AddressDetails>();
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: structuredFieldsRequired lets parent forms keep only address search mandatory while preserving legacy required markers elsewhere. */
  readonly structuredFieldsRequired = input<boolean | null>(null);
  /** PARENT-CHILD CONTRACT: showGeoFields documents the input state boundary used by this file. */
  readonly showGeoFields = input(false);
  /** PARENT-CHILD CONTRACT: searchLabel documents the input state boundary used by this file. */
  readonly searchLabel = input('');
  /** PARENT-CHILD CONTRACT: searchHint documents the input state boundary used by this file. */
  readonly searchHint = input('');
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: emptyMessage documents the input state boundary used by this file. */
  readonly emptyMessage = input('');
  readonly fieldErrors = input<Record<string, string>>({});
  readonly valueChange = output<AddressDetails>();
  /** STATE OWNER: lookupError documents the signal state boundary used by this file. */
  readonly lookupError = signal('');
  /** STATE OWNER: isSearching documents the signal state boundary used by this file. */
  readonly isSearching = signal(false);
  readonly options = signal<AddressLookupOption[]>([]);
  private addressCache = new Map<string, AddressDetails>();
  /** STATE OWNER: autocompleteOptions documents the computed state boundary used by this file. */
  readonly autocompleteOptions = computed(() =>
    this.options().map((option) => ({
      label: option.label,
      value: option.value,
      caption: option.caption
    }))
  );
  /** STATE OWNER: resolvedError documents the computed state boundary used by this file. */
  readonly resolvedError = computed(() => this.error() || this.lookupError());
  /** STATE OWNER: resolvedSearchLabel documents the computed state boundary used by this file. */
  readonly resolvedSearchLabel = computed(() => this.searchLabel() || this.t('shared.addressForm.searchLabel'));
  /** STATE OWNER: resolvedSearchHint documents the computed state boundary used by this file. */
  readonly resolvedSearchHint = computed(() => this.searchHint() || this.t('shared.addressForm.searchHint'));
  /** STATE OWNER: resolvedEmptyMessage documents the computed state boundary used by this file. */
  readonly resolvedEmptyMessage = computed(() => this.emptyMessage() || this.t('shared.addressForm.emptyMessage'));
  /** STATE OWNER: resolvedStructuredFieldsRequired follows the search required flag unless a parent overrides the structured field contract. */
  readonly resolvedStructuredFieldsRequired = computed(() => this.structuredFieldsRequired() ?? this.required());

  /**
   * BUSINESS LOGIC: onQueryChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onQueryChange(rawValue: string): void {
    const nextValue = rawValue.trim();
    const clearedAddressFields: Partial<AddressDetails> = nextValue
      ? {}
      : {
          streetAddress: '',
          locality: '',
          subLocality: '',
          region: '',
          country: '',
          postalCode: '',
          area: ''
        };
    const nextGeo: [number, number] | undefined = nextValue ? undefined : [0, 0];

    this.lookupError.set('');
    this.updateValue({ formattedAddress: rawValue, ...clearedAddressFields }, nextGeo);

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (nextValue.length < 3) {
      this.options.set([]);
      this.isSearching.set(false);
      return;
    }

    this.isSearching.set(true);
    this.searchTimer = setTimeout(() => {
      this.fetchAddressOptions(rawValue).catch(() => {
        this.isSearching.set(false);
        this.options.set([]);
        this.lookupError.set(this.t('shared.addressForm.errors.lookupUnavailable'));
      });
    }, 300);
  }

  /**
   * BUSINESS LOGIC: selectAddress
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async selectAddress(query: string): Promise<void> {
    this.lookupError.set('');

    const cached = this.addressCache.get(query);
    if (cached) {
      this.valueChange.emit(cached);
      this.options.set([]);
      this.addressCache.clear();
      return;
    }

    this.isSearching.set(true);
    try {
      const address = await this.addressLookupService.getStructuredAddress(query);

      if (!address) {
        this.lookupError.set(this.t('shared.addressForm.errors.noAddressDetails'));
        return;
      }

      this.valueChange.emit(address);
      this.options.set([]);
    } catch {
      this.lookupError.set(this.t('shared.addressForm.errors.lookupUnavailable'));
    } finally {
      this.isSearching.set(false);
    }
  }

  /**
   * BUSINESS LOGIC: setValue
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setValue(key: keyof AddressDetails, rawValue: string): void {
    if (key === 'geo') {
      return;
    }

    this.updateValue({ [key]: rawValue } as Partial<AddressDetails>);
  }

  /**
   * BUSINESS LOGIC: setGeo
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setGeo(index: 0 | 1, rawValue: string): void {
    const parsed = Number(rawValue);
    const current = this.value().geo;
    const geo: [number, number] = [current[0], current[1]];
    geo[index] = Number.isFinite(parsed) ? parsed : 0;
    this.updateValue({}, geo);
  }

  /**
   * BUSINESS LOGIC: updateValue
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private updateValue(partial: Partial<AddressDetails>, nextGeo?: [number, number]): void {
    this.valueChange.emit({
      ...this.value(),
      ...partial,
      geo: nextGeo ?? this.value().geo
    });
  }

  /**
   * BUSINESS LOGIC: fetchAddressOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private async fetchAddressOptions(query: string): Promise<void> {
    const options = await this.addressLookupService.search(query);
    this.addressCache.clear();
    for (const option of options) {
      if (option.structured) {
        this.addressCache.set(option.value, option.structured);
      }
    }
    this.options.set(options);
    this.isSearching.set(false);
  }

  /**
   * BUSINESS LOGIC: t
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private t(key: string): string {
    this.activeLanguage();
    return this.translateService.instant(key);
  }
}
